import { useState } from 'react';
import { DownloadIcon } from 'lucide-react';
import { Panel } from '../../ui/Panel';
import { CheckList } from '../../ui/CheckList';
import { Spinner } from '../../../ui/Spinner';
import { CopyButton } from '../../marketing/CopyButton';
import { DesignCanvas } from '../../marketing/DesignCanvas';
import { PostPreview } from '../PostPreview';
import { PlatformIcon } from '../PlatformIcon';
import { useDesignDownload } from '../../marketing/useDesignDownload';
import { btnGhost } from '../../ui/styles';
import type { Check } from '../../editing';
import type { MarketingDesign } from '../../../core/types/marketing';
import type { SocialPlatform, SocialPost } from '../../../core/types/social';
type Props = {
  post: SocialPost;
  design?: MarketingDesign;
  tokens: Record<string, string>;
  text: string;
  checks: Check[];
  avatar: string;
  handle: string;
};

/** Rechts in de editor: zo ziet het eruit per kanaal, tekst en beeld meenemen, en wat er nog mist. */
export function PostPreviewPanel({
  post,
  design,
  tokens,
  text,
  checks,
  avatar,
  handle
}: Props) {
  const [previewOn, setPreviewOn] = useState<SocialPlatform | null>(null);
  const {
    canvas,
    busy,
    download
  } = useDesignDownload();
  const platform = previewOn && post.platforms.includes(previewOn) ? previewOn : post.platforms[0] ?? 'Instagram-post';
  return <div className="space-y-6 lg:sticky lg:top-20" data-id="element-4308" data-mp="3drujf9vd4whc">
      <div className="rounded-lg bg-white/60 p-4 ring-1 ring-line sm:p-6" data-id="element-4309" data-mp="38tjb53kpuu18">
        {post.platforms.length > 1 &&
      // Op de telefoon één rij die zijwaarts schuift, in plaats van drie regels knoppen boven het voorbeeld.
      <div role="radiogroup" aria-label="Voorbeeld op" className="-mx-1 mb-4 flex gap-1.5 overflow-x-auto px-1 pb-1 sm:flex-wrap sm:justify-center" data-id="element-4310" data-mp="302anoo078zhj">
            {post.platforms.map(x => <button key={x} type="button" role="radio" aria-checked={platform === x} onClick={() => setPreviewOn(x)} className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] transition-colors duration-150 ease-soft ${platform === x ? 'bg-ink text-shell' : 'bg-white text-muted ring-1 ring-line hover:text-ink'}`} data-id="element-4311" data-mp="2q3bff6jcj5v2">
                <PlatformIcon platform={x} data-id="element-4312" data-mp="26ghz1dk9xvxx" />
                {x}
              </button>)}
          </div>}
        <PostPreview post={post} design={design} platform={platform} tokens={tokens} avatar={avatar} handle={handle} data-id="element-4313" data-mp="2u7dxgtj8jpe5" />
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2" data-id="element-4314" data-mp="2z81k730flk2k">
          {text && <CopyButton text={text} label="Kopieer tekst" data-id="element-4315" data-mp="2leabtxyfs68f" />}
          {design && <button type="button" onClick={() => download(design, {
          name: post.title,
          successTitle: 'Beeld gedownload',
          errorHint: 'Open het ontwerp en probeer het daar nog eens.'
        })} disabled={busy} className={`${btnGhost} text-ink disabled:text-muted`} data-id="element-4316" data-mp="2pet2hoolf691">
              {busy ? <Spinner className="h-3.5 w-3.5" data-id="element-4317" data-mp="11iwhh11xutsd" /> : <DownloadIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-4318" data-mp="11htpepsvqlav" />}
              Download beeld
            </button>}
          {!design && post.image && <a href={post.image} target="_blank" rel="noreferrer" download className={`${btnGhost} text-ink`} data-id="element-4319" data-mp="1b9h9u6r0r22u">
              <DownloadIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-4320" data-mp="niu15j9a48f8" />
              Open foto
            </a>}
        </div>
      </div>

      <Panel title="Klaar om te posten?" data-id="element-4321" data-mp="xh1cnmksty9u">
        <CheckList checks={checks} okText="Alles staat klaar. Kopieer, plaats en vink af." data-id="element-4322" data-mp="xeonaocpolmr" />
      </Panel>

      {/* Het ontwerp op ware grootte, buiten beeld, alleen voor de download. */}
      {design && <div aria-hidden="true" className="pointer-events-none fixed left-[-10000px] top-0" data-id="element-4323" data-mp="28xrekti6w3xn">
          <DesignCanvas ref={canvas} design={design} data-id="element-4324" data-mp="23yy316mgqn04" />
        </div>}
    </div>;
}